/**
 * Mordeczka.eu Admin - Add Season Page
 *
 * Standalone page for adding a new season to a series
 */

import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  Button,
  Form,
  InputNumber,
  Space,
  message,
  Typography,
} from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { seasonsApi, worksApi, type SeasonCreate } from "../services/api";

const { Title } = Typography;

export function AddSeasonPage() {
  const navigate = useNavigate();
  const { id: workId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  // Fetch work data to get series info and existing seasons
  const { data: work, isLoading: workLoading } = useQuery({
    queryKey: ["work", workId],
    queryFn: () => worksApi.get(workId as string),
    enabled: !!workId,
  });

  // Suggest next season number based on existing seasons
  const nextSeasonNumber = work?.series_details?.seasons?.length
    ? Math.max(...work.series_details.seasons.map((s) => s.season_number)) + 1
    : 1;

  // Create season mutation
  const createMutation = useMutation({
    mutationFn: (data: SeasonCreate) =>
      seasonsApi.create(workId as string, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work", workId] });
      queryClient.invalidateQueries({ queryKey: ["works"] });
      message.success("Sezon dodany pomyślnie");
      navigate("/series");
    },
    onError: (error: any) => {
      const detail = error.response?.data?.detail;
      message.error(detail || "Nie udało się dodać sezonu");
    },
  });

  // Calculate total duration from episodes if not provided
  const handleEpisodesChange = () => {
    const episodes = form.getFieldValue("episodes_count");
    const duration = form.getFieldValue("episode_duration_minutes");
    if (episodes && duration) {
      form.setFieldsValue({ total_duration_minutes: episodes * duration });
    }
  };

  const handleSubmit = (values: SeasonCreate) => {
    createMutation.mutate(values);
  };

  if (workLoading) {
    return <Card loading />;
  }

  if (!work) {
    return (
      <Card>
        <Typography.Text type="danger">Nie znaleziono serialu</Typography.Text>
      </Card>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/series")}
          style={{ padding: 0 }}
        >
          Powrót do listy seriali
        </Button>
        <Title level={2} style={{ marginTop: 8, marginBottom: 0 }}>
          Dodaj sezon do: {work.title}
        </Title>
      </div>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            season_number: nextSeasonNumber,
          }}
        >
          <Form.Item
            name="season_number"
            label="Numer sezonu"
            rules={[{ required: true, message: "Numer sezonu jest wymagany" }]}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="year" label="Rok emisji">
            <InputNumber
              min={1900}
              max={2100}
              style={{ width: "100%" }}
              placeholder="np. 2024"
            />
          </Form.Item>

          <Form.Item name="episodes_count" label="Liczba odcinków">
            <InputNumber
              min={1}
              style={{ width: "100%" }}
              onChange={handleEpisodesChange}
            />
          </Form.Item>

          <Form.Item
            name="episode_duration_minutes"
            label="Średni czas odcinka (minuty)"
          >
            <InputNumber
              min={1}
              style={{ width: "100%" }}
              onChange={handleEpisodesChange}
            />
          </Form.Item>

          <Form.Item
            name="total_duration_minutes"
            label="Łączny czas sezonu (minuty)"
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            style={{ marginBottom: 0, marginTop: 24, textAlign: "right" }}
          >
            <Space>
              <Button onClick={() => navigate("/series")}>Anuluj</Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={createMutation.isPending}
              >
                Dodaj sezon
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

export default AddSeasonPage;
